import React from 'react';
import { throttle } from 'throttle-debounce';
import mergeRefs from 'utils/merge-refs';
import happen from 'utils/happen';
import breakpoints from 'utils/breakpoints';
import type { StyledElement, AriaAttributes, TestAutomation, ResponsiveImageOutput } from 'contracts';

import Html from '../html';
import type { Typography } from '../html';

interface BackgroundImageElement
  extends StyledElement,
    AriaAttributes,
    TestAutomation,
    Typography,
    Pick<React.CSSProperties, 'backgroundSize' | 'backgroundPosition' | 'backgroundRepeat'> {
  mobileSrc?: ResponsiveImageOutput;
  src: ResponsiveImageOutput;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const BackgroundImage = React.forwardRef<HTMLDivElement, BackgroundImageElement>((props, ref) => {
  const {
    testId,
    src,
    mobileSrc,
    backgroundPosition,
    backgroundRepeat,
    backgroundSize,
    italic,
    uppercase,
    ellipsis,
    typography,
    fontWeight,
    className,
    style,
    children,
    onClick,
    ...rest
  } = props;
  const bgRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const handleWindowResize = throttle(50, (): void => {
      if (!bgRef.current || !src) return;

      const mobile = breakpoints.max('md');
      const image =
        mobile && mobileSrc
          ? mobileSrc.src
          : (src.images?.find((img) => happen(window.innerWidth).lessThanOrEqual(img.width))?.path ?? src.src);

      bgRef.current.style.backgroundImage = `url("${image}")`;
    });

    handleWindowResize();

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [src, mobileSrc]);

  return (
    <Html.div
      testId={testId}
      italic={italic}
      uppercase={uppercase}
      ellipsis={ellipsis}
      typography={typography}
      fontWeight={fontWeight}
      className={className}
      style={{ backgroundPosition, backgroundRepeat, backgroundSize, ...style }}
      arias={rest}
      ref={mergeRefs(ref, bgRef)}
      onClick={onClick}
    >
      {children}
    </Html.div>
  );
});

export type { BackgroundImageElement };
export default BackgroundImage;
